/** Admin — form-based editing of public Website Content:
 *  Tab 1: Public Workshops & Events cards
 *  Tab 2: Training Focus Areas tiles
 * All editing is fill-in-fields + Save (no code access). */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, ExternalLink } from "lucide-react";
import ImageUploadField from "@/components/ImageUploadField";
import RichTextEditor from "@/components/RichTextEditor";
import { FOCUS_ICONS, focusIcon } from "@/lib/focusIcons";

const Field = ({ label, children }) => (
  <div>
    <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#6B5944] mb-1.5">{label}</div>
    {children}
  </div>
);

/* ============================ Workshops & Events ============================ */

const EMPTY_WORKSHOP = {
  image_url: "", badge: "", title: "", tagline: "", description: "",
  date_text: "", location: "", price: "", button_text: "", button_link: "", published: true,
};

const WorkshopFormDialog = ({ open, onOpenChange, editing, onSaved }) => {
  const [form, setForm] = useState(EMPTY_WORKSHOP);
  const [saving, setSaving] = useState(false);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) setForm(editing ? { ...EMPTY_WORKSHOP, ...editing } : EMPTY_WORKSHOP);
  }, [open, editing]);

  const save = async () => {
    if (!form.title) {
      toast.error("Please give the workshop a title.");
      return;
    }
    setSaving(true);
    try {
      if (editing?.id) {
        await api.put(`/site-content/workshops/${editing.id}`, form);
        toast.success("Workshop card updated.");
      } else {
        await api.post("/site-content/workshops", form);
        toast.success("Workshop card created.");
      }
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#FDFBF5] border-[#EBE3D2]" data-testid="workshop-card-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#2A1810]">
            {editing?.id ? "Edit Workshop / Event" : "New Workshop / Event"}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#6B5944]">
            Fill in the fields and save — the card updates on the public page instantly.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <ImageUploadField
            value={form.image_url}
            onChange={(url) => setF("image_url", url)}
            label="Photo / image"
            testid="workshop-card-image"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Badge text (e.g. Coming soon, Open for registration)">
              <Input value={form.badge} onChange={(e) => setF("badge", e.target.value)} data-testid="workshop-card-badge" />
            </Field>
            <Field label="Title *">
              <Input value={form.title} onChange={(e) => setF("title", e.target.value)} data-testid="workshop-card-title" />
            </Field>
          </div>
          <Field label="Tagline">
            <Input value={form.tagline} onChange={(e) => setF("tagline", e.target.value)} placeholder="A short one-line hook" data-testid="workshop-card-tagline" />
          </Field>
          <Field label="Description">
            <RichTextEditor value={form.description} onChange={(v) => setF("description", v)} minHeight={140} testid="workshop-card-description" />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Date">
              <Input value={form.date_text} onChange={(e) => setF("date_text", e.target.value)} placeholder="e.g. March 14, 2026" data-testid="workshop-card-date" />
            </Field>
            <Field label="Location">
              <Input value={form.location} onChange={(e) => setF("location", e.target.value)} placeholder="e.g. Port of Spain" data-testid="workshop-card-location" />
            </Field>
            <Field label="Price / investment">
              <Input value={form.price} onChange={(e) => setF("price", e.target.value)} placeholder="e.g. TT$450" data-testid="workshop-card-price" />
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Button text">
              <Input value={form.button_text} onChange={(e) => setF("button_text", e.target.value)} placeholder="e.g. Register now" data-testid="workshop-card-button-text" />
            </Field>
            <Field label="Button link">
              <Input value={form.button_link} onChange={(e) => setF("button_link", e.target.value)} placeholder="https://… or /book#booking" data-testid="workshop-card-button-link" />
            </Field>
          </div>
          <div className="flex items-center justify-between bg-white border border-[#EBE3D2] rounded-xl px-4 py-3">
            <div className="text-sm font-medium text-[#2A1810]">Published (visible on the website)</div>
            <Switch checked={form.published} onCheckedChange={(v) => setF("published", v)} data-testid="workshop-card-published" />
          </div>
          <Button onClick={save} disabled={saving} className="w-full bg-[#5D3A1F] hover:bg-[#4A2E18]" data-testid="workshop-card-save">
            {saving ? "Saving…" : editing?.id ? "Save changes" : "Create card"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ============================ Training Focus Areas ============================ */

const EMPTY_AREA = { icon: "Sparkles", title: "", description: "" };

const FocusAreaFormDialog = ({ open, onOpenChange, editing, onSaved }) => {
  const [form, setForm] = useState(EMPTY_AREA);
  const [saving, setSaving] = useState(false);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) setForm(editing ? { ...EMPTY_AREA, ...editing } : EMPTY_AREA);
  }, [open, editing]);

  const save = async () => {
    if (!form.title) {
      toast.error("Please give the focus area a title.");
      return;
    }
    setSaving(true);
    try {
      if (editing?.id) {
        await api.put(`/site-content/focus-areas/${editing.id}`, form);
        toast.success("Focus area updated.");
      } else {
        await api.post("/site-content/focus-areas", form);
        toast.success("Focus area created.");
      }
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const Icon = focusIcon(form.icon);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-[#FDFBF5] border-[#EBE3D2]" data-testid="focus-area-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#2A1810]">
            {editing?.id ? "Edit Focus Area" : "New Focus Area"}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#6B5944]">
            Pick an icon, fill in the fields and save — the tile updates on the public page instantly.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Icon">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-[#C28A2E]/12 flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5 text-[#C28A2E]" strokeWidth={1.7} />
              </div>
              <Select value={form.icon} onValueChange={(v) => setF("icon", v)}>
                <SelectTrigger data-testid="focus-area-icon"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {Object.keys(FOCUS_ICONS).map((name) => {
                    const I = FOCUS_ICONS[name];
                    return (
                      <SelectItem key={name} value={name}>
                        <span className="inline-flex items-center gap-2">
                          <I size={14} className="text-[#C28A2E]" /> {name}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </Field>
          <Field label="Title *">
            <Input value={form.title} onChange={(e) => setF("title", e.target.value)} data-testid="focus-area-title" />
          </Field>
          <Field label="Description">
            <RichTextEditor value={form.description} onChange={(v) => setF("description", v)} minHeight={100} testid="focus-area-description" />
          </Field>
          <Button onClick={save} disabled={saving} className="w-full bg-[#5D3A1F] hover:bg-[#4A2E18]" data-testid="focus-area-save">
            {saving ? "Saving…" : editing?.id ? "Save changes" : "Create focus area"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ============================ Page ============================ */

export default function SiteContentAdminPage() {
  const [workshops, setWorkshops] = useState([]);
  const [areas, setAreas] = useState([]);
  const [workshopFormOpen, setWorkshopFormOpen] = useState(false);
  const [areaFormOpen, setAreaFormOpen] = useState(false);
  const [editingWorkshop, setEditingWorkshop] = useState(null);
  const [editingArea, setEditingArea] = useState(null);

  const load = () => {
    api.get("/site-content/workshops").then((r) => setWorkshops(r.data.workshops || [])).catch(() => {});
    api.get("/site-content/focus-areas").then((r) => setAreas(r.data.focus_areas || [])).catch(() => {});
  };
  useEffect(load, []);

  // Swap `order` with the neighbour to move an item up/down.
  const move = async (list, kind, index, dir) => {
    const j = index + dir;
    if (j < 0 || j >= list.length) return;
    const a = list[index], b = list[j];
    try {
      await Promise.all([
        api.put(`/site-content/${kind}/${a.id}`, { order: b.order }),
        api.put(`/site-content/${kind}/${b.id}`, { order: a.order }),
      ]);
      load();
    } catch {
      toast.error("Reorder failed.");
    }
  };

  const removeWorkshop = async (w) => {
    if (!window.confirm(`Delete "${w.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/site-content/workshops/${w.id}`);
      toast.success("Workshop card deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const removeArea = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/site-content/focus-areas/${a.id}`);
      toast.success("Focus area deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const MoveButtons = ({ list, kind, index }) => (
    <div className="flex flex-col">
      <button onClick={() => move(list, kind, index, -1)} disabled={index === 0} className="p-0.5 text-[#6B5944] hover:text-[#C28A2E] disabled:opacity-25" aria-label="Move up" data-testid={`move-up-${kind}-${index}`}>
        <ArrowUp size={13} />
      </button>
      <button onClick={() => move(list, kind, index, 1)} disabled={index === list.length - 1} className="p-0.5 text-[#6B5944] hover:text-[#C28A2E] disabled:opacity-25" aria-label="Move down" data-testid={`move-down-${kind}-${index}`}>
        <ArrowDown size={13} />
      </button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="site-content-admin-page">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-semibold text-[#2A1810]">Website Content</h1>
        <p className="text-sm text-[#6B5944] mt-1">
          Edit the public Workshops &amp; Events and Training Focus Areas cards ·{" "}
          <a href="/book#services" target="_blank" rel="noopener noreferrer" className="text-[#C28A2E] hover:underline">view public page</a>
        </p>
      </div>

      <Tabs defaultValue="workshops">
        <TabsList className="bg-[#F6F3EB]">
          <TabsTrigger value="workshops" data-testid="tab-workshop-cards">Workshops &amp; Events ({workshops.length})</TabsTrigger>
          <TabsTrigger value="focus-areas" data-testid="tab-focus-areas">Training Focus Areas ({areas.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="workshops" className="space-y-3 mt-4">
          <div className="flex justify-end">
            <Button onClick={() => { setEditingWorkshop(null); setWorkshopFormOpen(true); }} className="bg-[#5D3A1F] hover:bg-[#4A2E18]" data-testid="new-workshop-card-button">
              <Plus size={16} className="mr-1.5" /> New Workshop / Event
            </Button>
          </div>
          {workshops.length === 0 && (
            <div className="py-16 text-center text-[#6B5944] border border-dashed border-[#EBE3D2] rounded-2xl">
              No workshop or event cards yet — the public page shows “coming soon” placeholders until you add one.
            </div>
          )}
          {workshops.map((w, i) => (
            <div key={w.id} className="bg-white border border-[#EBE3D2] rounded-xl p-4 flex items-center gap-3 flex-wrap" data-testid={`admin-workshop-card-row-${w.id}`}>
              <MoveButtons list={workshops} kind="workshops" index={i} />
              {w.image_url && <img src={w.image_url} alt="" className="w-16 h-12 object-cover rounded-md hidden sm:block" />}
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-[#2A1810]">{w.title}</span>
                  {w.published ? (
                    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Published</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[#6B5944]">Hidden</Badge>
                  )}
                </div>
                <div className="text-xs text-[#6B5944] mt-1">
                  {[w.badge, w.date_text, w.location, w.price].filter(Boolean).join(" · ") || "No details yet"}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <a href="/book#events" target="_blank" rel="noopener noreferrer" className="p-2 text-[#6B5944] hover:text-[#C28A2E]" title="View section" data-testid={`view-workshop-card-${w.id}`}>
                  <ExternalLink size={15} />
                </a>
                <Button variant="outline" size="sm" onClick={() => { setEditingWorkshop(w); setWorkshopFormOpen(true); }} data-testid={`edit-workshop-card-${w.id}`}>
                  <Pencil size={14} />
                </Button>
                <Button variant="outline" size="sm" onClick={() => removeWorkshop(w)} className="text-red-600 hover:text-red-700" data-testid={`delete-workshop-card-${w.id}`}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="focus-areas" className="space-y-3 mt-4">
          <div className="flex justify-end">
            <Button onClick={() => { setEditingArea(null); setAreaFormOpen(true); }} className="bg-[#5D3A1F] hover:bg-[#4A2E18]" data-testid="new-focus-area-button">
              <Plus size={16} className="mr-1.5" /> New Focus Area
            </Button>
          </div>
          {areas.map((a, i) => {
            const Icon = focusIcon(a.icon);
            return (
              <div key={a.id} className="bg-white border border-[#EBE3D2] rounded-xl p-4 flex items-center gap-3 flex-wrap" data-testid={`admin-focus-area-row-${a.id}`}>
                <MoveButtons list={areas} kind="focus-areas" index={i} />
                <div className="w-10 h-10 rounded-xl bg-[#C28A2E]/12 flex items-center justify-center shrink-0">
                  <Icon className="w-4.5 h-4.5 text-[#C28A2E]" size={18} strokeWidth={1.7} />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <span className="font-semibold text-[#2A1810]">{a.title}</span>
                  <div className="text-xs text-[#6B5944] mt-0.5 line-clamp-1" dangerouslySetInnerHTML={{ __html: (a.description || "").replace(/<[^>]+>/g, " ") }} />
                </div>
                <div className="flex items-center gap-1.5">
                  <Button variant="outline" size="sm" onClick={() => { setEditingArea(a); setAreaFormOpen(true); }} data-testid={`edit-focus-area-${a.id}`}>
                    <Pencil size={14} />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => removeArea(a)} className="text-red-600 hover:text-red-700" data-testid={`delete-focus-area-${a.id}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            );
          })}
        </TabsContent>
      </Tabs>

      <WorkshopFormDialog open={workshopFormOpen} onOpenChange={setWorkshopFormOpen} editing={editingWorkshop} onSaved={load} />
      <FocusAreaFormDialog open={areaFormOpen} onOpenChange={setAreaFormOpen} editing={editingArea} onSaved={load} />
    </div>
  );
}
